import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { PAGE_SIZES, type TableState } from '../hooks/useTableState';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { formatNumber } from '../lib/format';
import { Button } from './primitives';
import { Icon } from './Icon';

/**
 * Tabla con paginación, ordenamiento y búsqueda.
 *
 * Dos modos: `server` delega en el backend (paginación real, imprescindible con
 * cientos de miles de filas) y `client` ordena en memoria para listas pequeñas
 * que ya llegaron completas.
 */
export interface Column<T> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  width?: string;
  render: (row: T) => ReactNode;
  /** Valor plano para ordenar y buscar en modo cliente. */
  plain?: (row: T) => string | number | null;
  help?: string;
}

export interface DataTableProps<T> {
  columns: Array<Column<T>>;
  rows: T[];
  getRowId: (row: T) => string;
  state: TableState;
  onStateChange: (next: TableState) => void;
  mode?: 'server' | 'client';
  total?: number;
  totalPages?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  toolbar?: ReactNode;
  emptyMessage?: string;
  caption: string;
  rowActions?: (row: T) => ReactNode;
  isFetching?: boolean;
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  const debounced = useDebouncedValue(draft, 350);

  useEffect(() => {
    if (debounced !== value) onChange(debounced);
    // `value` no entra en las dependencias: cambiaría al confirmar y reiniciaría el ciclo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <div className="input-group" style={{ maxWidth: 300 }}>
      <label className="sr-only" htmlFor="table-search">
        Buscar
      </label>
      <input
        id="table-search"
        className="input"
        type="search"
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
      />
      <span className="input-group__btn" aria-hidden="true">
        <Icon name="search" size={14} />
      </span>
    </div>
  );
}

function compare(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'es-CL', { numeric: true, sensitivity: 'base' });
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  state,
  onStateChange,
  mode = 'server',
  total,
  totalPages,
  searchable = true,
  searchPlaceholder = 'Buscar…',
  toolbar,
  emptyMessage = 'No hay filas que coincidan con los criterios actuales.',
  caption,
  rowActions,
  isFetching = false,
}: DataTableProps<T>): JSX.Element {
  const clientProcessed = useMemo(() => {
    if (mode !== 'client') return { rows, total: total ?? rows.length };

    const term = state.search.trim().toLocaleLowerCase('es-CL');
    let working = rows;

    if (term.length > 0) {
      working = working.filter((row) =>
        columns.some((column) => {
          const plain = column.plain?.(row);
          return plain !== null && plain !== undefined && String(plain).toLocaleLowerCase('es-CL').includes(term);
        }),
      );
    }

    if (state.sortBy !== null) {
      const column = columns.find((candidate) => candidate.key === state.sortBy);
      if (column?.plain) {
        const direction = state.sortDir === 'asc' ? 1 : -1;
        working = [...working].sort(
          (a, b) => compare(column.plain?.(a) ?? null, column.plain?.(b) ?? null) * direction,
        );
      }
    }

    const from = (state.page - 1) * state.pageSize;
    return { rows: working.slice(from, from + state.pageSize), total: working.length };
  }, [mode, rows, columns, state, total]);

  const visibleRows = mode === 'client' ? clientProcessed.rows : rows;
  const rowCount = mode === 'client' ? clientProcessed.total : (total ?? rows.length);
  const pageCount =
    mode === 'client'
      ? Math.max(1, Math.ceil(rowCount / state.pageSize))
      : (totalPages ?? Math.max(1, Math.ceil(rowCount / state.pageSize)));

  const firstRow = rowCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
  const lastRow = Math.min(state.page * state.pageSize, rowCount);

  const toggleSort = (key: string): void => {
    if (state.sortBy === key) {
      onStateChange({ ...state, sortDir: state.sortDir === 'asc' ? 'desc' : 'asc', page: 1 });
    } else {
      onStateChange({ ...state, sortBy: key, sortDir: 'asc', page: 1 });
    }
  };

  return (
    <div>
      {(searchable || toolbar !== undefined) && (
        <div className="table-toolbar">
          {searchable && (
            <SearchBox
              value={state.search}
              placeholder={searchPlaceholder}
              onChange={(search) => onStateChange({ ...state, search, page: 1 })}
            />
          )}
          {toolbar}
          {isFetching && (
            <span className="inline tiny muted" role="status">
              <span className="spinner" aria-hidden="true" /> Actualizando…
            </span>
          )}
        </div>
      )}

      <div className="table-scroll">
        <table className="table">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = state.sortBy === column.key;
                const ariaSort = active ? (state.sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
                return (
                  <th
                    key={column.key}
                    scope="col"
                    data-align={column.align ?? 'left'}
                    style={column.width !== undefined ? { width: column.width } : undefined}
                    aria-sort={column.sortable === true ? ariaSort : undefined}
                    title={column.help}
                  >
                    {column.sortable === true ? (
                      <button type="button" onClick={() => toggleSort(column.key)}>
                        {column.header}
                        <span className={`table__sort${active ? ' table__sort--on' : ''}`} aria-hidden="true">
                          {active ? (state.sortDir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      <span className="th-inner">{column.header}</span>
                    )}
                  </th>
                );
              })}
              {rowActions !== undefined && (
                <th scope="col" data-align="right" style={{ width: '1%' }}>
                  <span className="th-inner">Acciones</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (rowActions !== undefined ? 1 : 0)}>
                  <p className="muted" style={{ padding: '18px 2px', textAlign: 'center' }}>
                    {emptyMessage}
                  </p>
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => (
                <tr key={getRowId(row)}>
                  {columns.map((column) => (
                    <td key={column.key} data-align={column.align ?? 'left'}>
                      {column.render(row)}
                    </td>
                  ))}
                  {rowActions !== undefined && (
                    <td data-align="right">
                      <div className="row-actions">{rowActions(row)}</div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-foot">
        <span aria-live="polite">
          {rowCount === 0
            ? 'Sin filas'
            : `${formatNumber(firstRow)}–${formatNumber(lastRow)} de ${formatNumber(rowCount)}`}
        </span>
        <label className="inline tiny">
          Filas
          <select
            className="select"
            style={{ minWidth: 72, padding: '4px 7px' }}
            value={state.pageSize}
            onChange={(event) =>
              onStateChange({ ...state, pageSize: Number(event.target.value), page: 1 })
            }
            aria-label="Filas por página"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <span className="table-foot__spacer" />
        <Button
          size="sm"
          variant="ghost"
          icon="chevron-left"
          disabled={state.page <= 1}
          onClick={() => onStateChange({ ...state, page: state.page - 1 })}
          aria-label="Página anterior"
        >
          Anterior
        </Button>
        <span className="tiny">
          Página {formatNumber(state.page)} de {formatNumber(pageCount)}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={state.page >= pageCount}
          onClick={() => onStateChange({ ...state, page: state.page + 1 })}
          aria-label="Página siguiente"
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}
